import "./DealApplyWorkflowTemplatePanel.css";

import { TemplateConstants } from "../../../../../../../../../constants";
import { GetWorkflowTemplateResponse } from "../../../../../../../../../service/query";
import { hasDealDateValidationError, stringKeyToSentenceCase } from "../../../../../../../../../utils";
import {
  Autocomplete,
  Button,
  Column,
  DatePicker,
  Divider,
  ErrorBox,
  FlyoutPanel,
  KanaTooltip,
  Row,
  Toast,
} from "../../../../../../../../../widget";
import { getKeyDateLabel } from "../../../../../../../../asset-manager/new-deal-journey/DealWorkflow/utils/dealWorkflowUtils";
import { useDealApplyWorkflowTemplatePanel } from "./useDealApplyWorkflowTemplatePanel";

interface DealApplyWorkflowTemplatePanelProps {
  defaultDealUuid?: string;
  show: boolean;
  workflowTemplate: GetWorkflowTemplateResponse | undefined;
  workflowTemplateUuid: string | undefined;
  onClose: () => void;
}

export const DealApplyWorkflowTemplatePanel = ({
  defaultDealUuid,
  show,
  workflowTemplate,
  workflowTemplateUuid,
  onClose,
}: DealApplyWorkflowTemplatePanelProps): JSX.Element => {
  const {
    dealOptions,
    errorFieldKeys,
    errors,
    handleClearSelection,
    handleClose,
    handleSave,
    handleSearchDeals,
    handleSelectDeal,
    handleStageDateChange,
    isSaving,
    orderedDealPhases,
    selectedDealUuid,
    stageDates,
  } = useDealApplyWorkflowTemplatePanel({
    defaultDealUuid,
    onClose,
    workflowTemplate,
    workflowTemplateUuid,
  });

  return (
    <FlyoutPanel
      show={show}
      onClose={handleClose}
      size="Large"
      header="Apply workflow template to a deal"
      buttonsChildren={undefined}
      contentChildren={
        <div className="DealApplyWorkflowTemplatePanel">
          <p className="DealApplyWorkflowTemplatePanelIntro body1">
            When applying the selected workflow to a new deal, the template set of stages, steps, and related tasks will
            be assigned to the deal based on the current date. Stage date ranges provided below can be indicative at the
            point of creation and refined later as the deal develops.
          </p>

          {!defaultDealUuid && (
            <div className="DealApplyWorkflowTemplatePanelSearchControls">
              <div className="DealApplyWorkflowTemplatePanelSearch">
                <Autocomplete
                  label="Search deals"
                  placeholder=""
                  initialKey={selectedDealUuid}
                  data={dealOptions}
                  corners="rounded"
                  onSelect={handleSelectDeal}
                  onTypeSearch={(value) => {
                    handleSearchDeals(value).catch((error) => {
                      Toast.error({ message: error instanceof Error ? error.message : "Unable to load deals" });
                    });
                  }}
                />
              </div>
              {selectedDealUuid && (
                <Button
                  text="Clear"
                  variant="tertiary"
                  onClick={handleClearSelection}
                  disabled={isSaving}
                  size="small"
                  className="ApplyWorkflowTemplatePanelClearButton"
                />
              )}
            </div>
          )}

          <Divider type="horizontal" />

          <h2 className="DealApplyWorkflowTemplatePanelSectionTitle">Stage dates</h2>

          {errors.length > 0 && (
            <Row>
              <Column span={8}>
                <ErrorBox text={errors.map((error) => error.message)} />
              </Column>
            </Row>
          )}

          <div className="DealApplyWorkflowTemplatePanelStages">
            {orderedDealPhases.map((phaseItem, index) => (
              <div key={phaseItem.phase}>
                <section className="DealApplyWorkflowTemplatePanelStage">
                  <div className="DealApplyWorkflowTemplatePanelStageHeader">
                    <h3 className="DealApplyWorkflowTemplatePanelStageTitle">
                      {stringKeyToSentenceCase(phaseItem.phase)}
                    </h3>
                  </div>

                  <div className="DealApplyWorkflowTemplatePanelStageDates">
                    {phaseItem.keyDates.map((keyDate) => (
                      <DatePicker
                        key={`${phaseItem.phase}-${keyDate}`}
                        label={getKeyDateLabel(keyDate)}
                        value={stageDates[phaseItem.phase]?.[keyDate]}
                        onChange={(value) => handleStageDateChange(phaseItem.phase, keyDate, value)}
                        corners="rounded"
                        hasError={hasDealDateValidationError(errorFieldKeys, phaseItem.phase, keyDate)}
                        tooltip={
                          <KanaTooltip tooltipHeader="" tooltipText={TemplateConstants.DEAL_KEY_DATE_TOOLTIP_TEXT} />
                        }
                      />
                    ))}
                  </div>
                </section>

                {index < orderedDealPhases.length - 1 && (
                  <Row spacingV="l">
                    <Column span={12}>
                      <Divider type="horizontal" />
                    </Column>
                  </Row>
                )}
              </div>
            ))}
          </div>

          <div className="DealApplyWorkflowTemplatePanelActions">
            <Button
              text="Save"
              type="button"
              variant="primary"
              size="smallLarge"
              corners="rounded"
              className="DealApplyWorkflowTemplatePanelSaveButton"
              onClick={() => {
                handleSave();
              }}
              isLoading={isSaving}
              disabled={!selectedDealUuid || !workflowTemplateUuid}
            />
          </div>
        </div>
      }
    />
  );
};
